import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { Box, ButtonBase, Collapse, Grid, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Swal from "sweetalert2";
import {
  CATEGORIES,
  HAKLAI_ADD_MODES,
  normalizeHaklaiCurrencyForDate
} from "../shared/haklaiConstants";
import {
  getHaklaiComputedAcquisitionByPart,
  getHaklaiPartDisplayLabel,
  isHaklaiPre1997MinimalZabanRelevantForPart,
  isOptionalPartCategory
} from "../shared/haklaiFlowHelpers";
import {
  getHaklaiAcquisitionDisplayDate,
  isHaklaiPre1997AcquisitionDateEligible
} from "../preCalc/logic/haklaiPreCalcHelpers";
import {
  HAKLAI_PROPERTY_DEPRECIATION_MODE
} from "../shared/haklaiDepreciationHelpers";
import {
  HAKLAI_DEPRECIATION_ACTIONS,
  HAKLAI_DEPRECIATION_DIALOG,
  HAKLAI_DEPRECIATION_LABELS,
  HAKLAI_DEPRECIATION_TOOLTIPS
} from "../shared/haklaiDepreciationTexts";
import HaklaiPropertyPartsTable from "./components/HaklaiPropertyPartsTable";
import { HaklaiPropertyPartRow } from "./components/HaklaiPropertyPartRows";
import HaklaiPropertyDepreciationDialog from "../shared/depreciation/HaklaiPropertyDepreciationDialog";
import { getHaklaiPropertyDepreciationUiState } from "../shared/depreciation/haklaiPropertyDepreciationUiState";
import {
  buildPropertyPartGroups,
  buildPropertyPartsComputationState,
  buildPropertyPartsValidationOverlay,
  findNextResidentialSlot,
  getCategoryAddState,
  getCategoryRows,
  getHighlightedCategoryId,
  getNextCategoryKey,
  getPropertyPartRowValidationState,
  getSectionAddMode,
  parsePropertyPartFieldValue
} from "./logic/haklaiPropertyPartsHelpers";
import {
  useHaklaiPropertyPartsStyles
} from "./haklaiPropertyPartsStyles";
import {
  getHaklaiPropertyPartAddAriaLabel,
  HAKLAI_PROPERTY_PART_ERRORS,
  HAKLAI_PROPERTY_PART_LABELS
} from "./haklaiPropertyPartsTexts";
import useHaklaiPropertyPartsController from "./hooks/useHaklaiPropertyPartsController";
import HaklaiLockNotice from "../shared/ui/HaklaiLockNotice";

/**
 * Owns the Haklai property-parts section.
 *
 * Responsibilities:
 * - renders the DMM/residential/commercial/land part tables
 * - coordinates part validation, highlighting, and add-row behavior
 * - opens purchase depreciation details for each relevant part
 *
 * Row/table UI belongs in `components/`; category rules and derivations belong
 * in `logic/`; controller-only state belongs in `hooks/`.
 */

// Builds the `bodies` array for a section that has two subcategories
// (primary + secondary). The ordering follows a single rule so that
// "חלקה א׳" and "חלקה ב+ג" stay symmetric:
//
//   - if the primary has rows, it leads and the secondary (if any) follows;
//   - if only the secondary has rows, it takes the lead slot — prevents
//     rendering an empty primary label above a populated secondary body;
//   - if both are empty, the primary still leads with an empty body so the
//     section shows a coherent empty state for its main category.
//
// Replaces the previous asymmetric code where Section A defaulted its empty
// state to commercial while Section BG (correctly) defaulted to land.
const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};
const PROPERTY_PARTS_SECTION_TRANSITION_MS = 180;

const getPropertyPartFieldDomName = (rowKey, fieldName) => {
  if (!rowKey || !fieldName) return "";
  if (fieldName === "label") return `${rowKey}_assetType`;
  if (fieldName === "value") return `${rowKey}_value`;
  if (fieldName === "valueWithoutZaban") return `${rowKey}_valueWithoutZaban`;
  if (fieldName === "purchaseDepreciation") return `${rowKey}_purchaseDepreciation`;
  return "";
};

const scrollFieldIntoView = (rootElement, fieldName) => {
  if (!rootElement || !fieldName) return false;
  const fieldElement = rootElement.querySelector(`[name="${fieldName}"], [data-field="${fieldName}"]`);
  if (!fieldElement) return false;
  const targetCell = fieldElement.closest("td");
  const scrollTarget = targetCell || fieldElement.closest(".MuiFormControl-root") || fieldElement;
  const horizontalScrollContainer = scrollTarget.closest(".MuiTableContainer-root");

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (
    horizontalScrollContainer
    && horizontalScrollContainer.scrollWidth > horizontalScrollContainer.clientWidth
  ) {
    const containerRect = horizontalScrollContainer.getBoundingClientRect();
    const targetRect = scrollTarget.getBoundingClientRect();
    const delta =
      (targetRect.left + targetRect.width / 2)
      - (containerRect.left + containerRect.width / 2);

    if (Math.abs(delta) > 1) {
      horizontalScrollContainer.scrollBy({
        left: delta,
        behavior: "smooth"
      });
    }
  }

  const focusTarget =
    scrollTarget.querySelector?.('[role="button"], input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])')
    || fieldElement;

  if (typeof focusTarget.focus === "function") {
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (error) {
      focusTarget.focus();
    }
  }

  return true;
};

const buildSectionBodies = (primary, secondary) => {
  const primaryHasRows = primary.rows.length > 0;
  const secondaryHasRows = secondary.rows.length > 0;

  if (!primaryHasRows && secondaryHasRows) {
    return [{
      bodyKey: `${secondary.bodyKey}Primary`,
      rows: secondary.rows,
      subSectionLabel: secondary.label
    }];
  }

  const bodies = [{
    bodyKey: primary.bodyKey,
    rows: primary.rows,
    subSectionLabel: primary.label
  }];
  if (primaryHasRows && secondaryHasRows) {
    bodies.push({
      bodyKey: secondary.bodyKey,
      rows: secondary.rows,
      subSectionLabel: secondary.label
    });
  }
  return bodies;
};

const EMPTY_ROW_FIELDS = Object.freeze({});
const PROPERTY_PART_VALUE_REMAINING_MESSAGE = HAKLAI_PROPERTY_PART_ERRORS.remainingValue;

const HaklaiPropertyPartsSection = React.memo(forwardRef((props, ref) => {
  const classes = useHaklaiPropertyPartsStyles();
  const sectionRootRef = useRef(null);
  const [allocationIssue, setAllocationIssue] = useState(null);
  const [activePurchaseDepreciationPartKey, setActivePurchaseDepreciationPartKey] = useState("");
  const [activePurchaseDepreciationMode, setActivePurchaseDepreciationMode] = useState("");
  const [pendingRentalDepreciationByPart, setPendingRentalDepreciationByPart] = useState({});
  const deferredAddTimerRef = useRef(null);
  const handleAddRow = props.handleAddRow;
  const handlePropertyPartOnChange = props.handlePropertyPartOnChange;
  const handlePurchaseDepreciationChange = props.handlePurchaseDepreciationChange;
  const parts = useMemo(() => props.propertyPartsData || {}, [props.propertyPartsData]);
  const totalPropertyCost = Number(props.totalPropertyCost) || 0;
  const totalAcquisitionCostValue = Number(props.totalAcquisitionCost);
  const totalAcquisitionCost = Number.isFinite(totalAcquisitionCostValue) && totalAcquisitionCostValue > 0
    ? totalAcquisitionCostValue
    : 0;
  const hasZaban = props.hasZaban === true;
  const ptorType = props.ptorType || "none";
  const hasTzinunOnPtor = props.hasTzinunOnPtor === true;
  const groups = useMemo(() => buildPropertyPartGroups(parts), [parts]);
  const allRows = groups.allRows;
  const residentialRentalByPart = useMemo(
    () => props.residentialRentalByPart || {},
    [props.residentialRentalByPart]
  );
  const purchaseDepreciationByPart = useMemo(
    () => props.purchaseDepreciationByPart || {},
    [props.purchaseDepreciationByPart]
  );
  const markRentalDepreciationPending = useCallback((partKey) => {
    if (!partKey) return;
    setPendingRentalDepreciationByPart((prev) => (
      prev[partKey] ? prev : { ...prev, [partKey]: true }
    ));
  }, []);
  const clearPendingRentalDepreciation = useCallback((partKey) => {
    if (!partKey) return;
    setPendingRentalDepreciationByPart((prev) => {
      if (!prev[partKey]) return prev;
      const next = { ...prev };
      delete next[partKey];
      return next;
    });
  }, []);
  const purchaseDateValue = useMemo(() => getHaklaiAcquisitionDisplayDate({
    wayOfGettingProperty: props.wayOfGettingProperty,
    acquisitionDetails: props.acquisitionDetails || {}
  }), [props.acquisitionDetails, props.wayOfGettingProperty]);
  const purchaseDepreciationStartDate = props.purchaseDepreciationStartDate || purchaseDateValue;
  const purchaseCurrencyValue = useMemo(() => normalizeHaklaiCurrencyForDate(
    purchaseDateValue,
    props.acquisitionDetails?.deduCurrency || "newShekel"
  ), [props.acquisitionDetails?.deduCurrency, purchaseDateValue]);
  const computedAcquisitionByPart = useMemo(
    () => getHaklaiComputedAcquisitionByPart(parts, props.acquisitionDetails?.amount),
    [parts, props.acquisitionDetails?.amount]
  );

  useEffect(() => {
    setPendingRentalDepreciationByPart((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((partKey) => {
        const savedRentalProfile = residentialRentalByPart?.[partKey];
        const hasSavedRentalProfile = (
          savedRentalProfile
          && typeof savedRentalProfile === "object"
          && Object.keys(savedRentalProfile).length > 0
        );
        if (!parts[partKey] || hasSavedRentalProfile) {
          delete next[partKey];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [parts, residentialRentalByPart]);

  const controller = useHaklaiPropertyPartsController({
    expanded: props.expanded,
    locked: props.locked,
    parts
  });
  const {
    committedRows,
    touchedFieldsByRow,
    stickyHeaderEnabled,
    sectionBgAddMenuAnchorEl,
    lockableContentRef,
    flashingRowKey,
    setStickyHeaderEnabled,
    setValueInputRef,
    setBuiltValueInputRef,
    setRowRef,
    touchRowField,
    commitAllRows,
    openSectionBgAddMenuAnchor,
    closeSectionBgAddMenu,
    scrollToRow,
    flashRowWhenVisible
  } = controller;

  const rowValidationStateByKey = useMemo(() => allRows.reduce((acc, [rowKey, part]) => {
    acc[rowKey] = getPropertyPartRowValidationState({
      rowKey,
      part,
      categoryRows: getCategoryRows(groups, part.category),
      hasZaban
    });
    return acc;
  }, {}), [allRows, groups, hasZaban]);

  const getRowValidationState = useCallback((rowKey, part) => (
    rowValidationStateByKey[rowKey] || getPropertyPartRowValidationState({
      rowKey,
      part,
      categoryRows: getCategoryRows(groups, part.category),
      hasZaban
    })
  ), [groups, hasZaban, rowValidationStateByKey]);

  const computationState = useMemo(() => buildPropertyPartsComputationState({
    groups,
    totalPropertyCost,
    totalAcquisitionCost
  }), [groups, totalAcquisitionCost, totalPropertyCost]);
  const allocationRevisionKey = useMemo(() => (
    [
      totalPropertyCost,
      ...allRows.map(([rowKey, part]) => `${rowKey}:${part?.value ?? ""}`)
    ].join("|")
  ), [allRows, totalPropertyCost]);

  // Pure overlay build — lives here (not inside the controller hook) so it
  // can read `computationState.runningRemainingByKey` and emit the
  // "value overshoot" cell error in lockstep with the rest of the field
  // errors. Single source of truth for every reveal rule.
  const validationOverlay = useMemo(() => buildPropertyPartsValidationOverlay({
    groups,
    touchedFieldsByRow,
    committedRows,
    hasZaban,
    runningRemainingByKey: computationState.runningRemainingByKey,
    validationStateByKey: rowValidationStateByKey
  }), [
    committedRows,
    computationState.runningRemainingByKey,
    groups,
    hasZaban,
    rowValidationStateByKey,
    touchedFieldsByRow
  ]);

  const allocationIssueCellErrors = useMemo(() => {
    if (allocationIssue?.type !== "remaining" || !allocationIssue.rowKey) return EMPTY_ROW_FIELDS;
    if (allocationIssue.revisionKey !== allocationRevisionKey) return EMPTY_ROW_FIELDS;
    const remaining = Number(computationState.runningRemainingByKey?.[allocationIssue.rowKey]);
    if (!(remaining > 0)) return EMPTY_ROW_FIELDS;
    return { value: PROPERTY_PART_VALUE_REMAINING_MESSAGE };
  }, [
    allocationIssue,
    allocationRevisionKey,
    computationState.runningRemainingByKey
  ]);

  const revealRowField = useCallback((rowKey, fieldName = "") => {
    if (!rowKey) return false;
    if (fieldName) touchRowField(rowKey, fieldName);
    afterNextPaint(() => {
      if (fieldName) {
        const didScroll = scrollFieldIntoView(
          sectionRootRef.current,
          getPropertyPartFieldDomName(rowKey, fieldName)
        );
        if (!didScroll) scrollToRow(rowKey);
      } else {
        scrollToRow(rowKey);
      }
      flashRowWhenVisible(rowKey);
    });
    return true;
  }, [flashRowWhenVisible, scrollToRow, touchRowField]);

  const getFirstInvalidPropertyPartTarget = useCallback(() => {
    const renderableRows = groups.renderableRows || [];
    if (!renderableRows.length) return null;
    const overshootRowKey = renderableRows.find(
      ([rowKey]) => Number(computationState.runningRemainingByKey?.[rowKey]) < 0
    )?.[0] || "";

    for (const [rowKey, part] of renderableRows) {
      if (rowKey === overshootRowKey) return { rowKey, fieldName: "value" };

      const rowValidationState = getRowValidationState(rowKey, part);
      if (rowValidationState.firstInvalidField) {
        return { rowKey, fieldName: rowValidationState.firstInvalidField };
      }
    }

    return null;
  }, [
    computationState.runningRemainingByKey,
    getRowValidationState,
    groups.renderableRows
  ]);

  const revealFirstInvalidField = useCallback(() => {
    const target = getFirstInvalidPropertyPartTarget();
    if (!target) return false;
    return revealRowField(target.rowKey, target.fieldName);
  }, [getFirstInvalidPropertyPartTarget, revealRowField]);

  const revealAllocationIssue = useCallback((issue = {}) => {
    const rowKey = issue.rowKey || "";
    setAllocationIssue(rowKey ? { ...issue, revisionKey: allocationRevisionKey } : null);
    if (rowKey) {
      revealRowField(rowKey, "value");
      return true;
    }
    afterNextPaint(() => {
      sectionRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
    return true;
  }, [allocationRevisionKey, revealRowField]);

  useEffect(() => {
    if (!allocationIssue?.rowKey) return;
    if (
      allocationIssue.type === "remaining"
      && allocationIssue.revisionKey !== allocationRevisionKey
    ) {
      setAllocationIssue(null);
      return;
    }
    const remaining = computationState.runningRemainingByKey?.[allocationIssue.rowKey];
    if (allocationIssue.type === "overshoot" && Number(remaining) < 0) return;
    if (
      allocationIssue.type === "remaining"
      && allocationIssue.rowKey === computationState.lastFilledRowKey
      && Number(remaining) > 0
    ) {
      return;
    }
    setAllocationIssue(null);
  }, [
    allocationIssue,
    allocationRevisionKey,
    computationState.lastFilledRowKey,
    computationState.runningRemainingByKey
  ]);

  const canAskPre1997Construction = useMemo(
    () => isHaklaiPre1997AcquisitionDateEligible({
      wayOfGettingProperty: props.wayOfGettingProperty,
      acquisitionDetails: props.acquisitionDetails || {}
    }),
    [props.acquisitionDetails, props.wayOfGettingProperty]
  );

  const getPurchaseDepreciationUiState = useCallback((partKey, part = {}) => (
    getHaklaiPropertyDepreciationUiState({
      partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      soldPropertyDate: props.soldPropertyDate || "",
      purchaseDepreciationStartDate,
      acquisitionAmount: computedAcquisitionByPart[partKey]
    })
  ), [
    computedAcquisitionByPart,
    props.soldPropertyDate,
    purchaseDepreciationByPart,
    purchaseDepreciationStartDate,
    residentialRentalByPart
  ]);

  const handleOpenPurchaseDepreciation = useCallback((partKey, part, state = null) => {
    const resolvedState = state || getPurchaseDepreciationUiState(partKey, part);
    if (!partKey || !resolvedState.isRelevant) return;
    setActivePurchaseDepreciationPartKey(partKey);
    setActivePurchaseDepreciationMode(resolvedState.primaryMode);
  }, [getPurchaseDepreciationUiState]);

  const handlePurchaseDepreciationDialogClose = useCallback(() => {
    if (
      activePurchaseDepreciationPartKey
      && activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      && !residentialRentalByPart?.[activePurchaseDepreciationPartKey]
    ) {
      clearPendingRentalDepreciation(activePurchaseDepreciationPartKey);
    }
    setActivePurchaseDepreciationPartKey("");
    setActivePurchaseDepreciationMode("");
  }, [
    activePurchaseDepreciationMode,
    activePurchaseDepreciationPartKey,
    clearPendingRentalDepreciation,
    residentialRentalByPart
  ]);

  const handleRentalDepreciationToggle = useCallback((partKey, part, checked, state = null) => {
    const resolvedState = state || getPurchaseDepreciationUiState(partKey, part);
    if (!partKey || resolvedState.isResidentialAutoAllowed !== true) return;

    if (checked) {
      markRentalDepreciationPending(partKey);
      handleOpenPurchaseDepreciation(partKey, part, {
        ...resolvedState,
        primaryMode: HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      });
      return;
    }

    if (!resolvedState.hasRentalConfigured) {
      clearPendingRentalDepreciation(partKey);
      return;
    }

    Swal.fire({
      title: HAKLAI_DEPRECIATION_DIALOG.clearRentalTitle,
      text: HAKLAI_DEPRECIATION_DIALOG.clearRentalText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: HAKLAI_DEPRECIATION_ACTIONS.delete,
      cancelButtonText: HAKLAI_DEPRECIATION_ACTIONS.cancel,
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) return;
      clearPendingRentalDepreciation(partKey);
      handlePurchaseDepreciationChange?.(
        partKey,
        {},
        HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
      );
    });
  }, [
    clearPendingRentalDepreciation,
    getPurchaseDepreciationUiState,
    handleOpenPurchaseDepreciation,
    handlePurchaseDepreciationChange,
    markRentalDepreciationPending
  ]);

  const getFirstInvalidPurchaseDepreciationPartKey = useCallback(() => {
    for (const [rowKey, part] of groups.renderableRows || []) {
      const state = getPurchaseDepreciationUiState(rowKey, part);
      if (state.isResidentialAutoAllowed === true && state.hasInvalid) return rowKey;
    }
    return "";
  }, [getPurchaseDepreciationUiState, groups.renderableRows]);

  const scrollToFirstPurchaseDepreciationIssue = useCallback(() => {
    const rowKey = getFirstInvalidPurchaseDepreciationPartKey();
    if (!rowKey) return false;
    revealRowField(rowKey, "purchaseDepreciation");
    return true;
  }, [getFirstInvalidPurchaseDepreciationPartKey, revealRowField]);

  const scrollToSection = useCallback(() => {
    afterNextPaint(() => {
      sectionRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
    return true;
  }, []);

  // Expose a minimal imperative surface so the parent (HaklaiCalcView) can
  // progressively reveal validation when the user clicks Analyze/Save.
  useImperativeHandle(ref, () => ({
    commitAllRows,
    revealFirstInvalidField,
    revealAllocationIssue,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  }), [
    commitAllRows,
    revealFirstInvalidField,
    revealAllocationIssue,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  ]);
  const showBuiltValueColumn = hasZaban && groups.residentialRows.length > 0;
  const showZabanColumn = hasZaban;
  const hasPre1997ConstructionQuestion = useMemo(() => (
    canAskPre1997Construction
    && hasZaban
    && groups.residentialRows.some(([, part]) => (
      isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
    ))
  ), [canAskPre1997Construction, groups.residentialRows, hasZaban]);

  const getEditableNameFieldState = useCallback((rowKey, validationState) => {
    const isCommitted = Boolean(committedRows[rowKey]);
    const fieldTouched = Boolean(touchedFieldsByRow[rowKey]?.label);
    const showRequiredError = (
      (isCommitted || fieldTouched)
      && validationState.readinessMissingFields.includes("label")
    );

    return {
      hasError: showRequiredError,
      helperText: "",
      isOptionalTrailing: validationState.isVisuallyDrafting
    };
  }, [committedRows, touchedFieldsByRow]);

  const residentialAddState = useMemo(() => getCategoryAddState({
    categoryId: CATEGORIES.RESIDENTIAL,
    propertyPartsData: parts
  }), [parts]);

  const landAddState = useMemo(() => getCategoryAddState({
    categoryId: CATEGORIES.LAND_AND_SHARED,
    propertyPartsData: parts
  }), [parts]);

  const commercialBgAddState = useMemo(() => getCategoryAddState({
    categoryId: CATEGORIES.COMMERCIAL_BG,
    propertyPartsData: parts
  }), [parts]);

  // Product currently allows adding only residential rows in חלקה א׳.
  // Legacy `commercial_*` rows may still render if loaded from existing data,
  // but the UI no longer offers a way to create new ones there.
  const sectionBgAddStates = useMemo(
    () => [commercialBgAddState, landAddState],
    [commercialBgAddState, landAddState]
  );
  const sectionAAddMode = residentialAddState.mode;
  const sectionBgAddMode = useMemo(() => getSectionAddMode(sectionBgAddStates), [sectionBgAddStates]);
  const sectionBgHighlightedCategoryId = useMemo(() => getHighlightedCategoryId([
    [CATEGORIES.COMMERCIAL_BG, commercialBgAddState],
    [CATEGORIES.LAND_AND_SHARED, landAddState]
  ]), [commercialBgAddState, landAddState]);

  // Guards against adding a row into a category that is already saturated.
  // The parent `handleAddRow(category)` is self-sufficient (it derives the
  // next key internally for every category), so we only need to confirm a
  // slot is free before delegating.
  const canAppendRowToCategory = useCallback((categoryId) => {
    if (categoryId === CATEGORIES.RESIDENTIAL) {
      return Boolean(findNextResidentialSlot(parts));
    }
    return Boolean(getNextCategoryKey({ categoryId, propertyPartsData: parts }));
  }, [parts]);

  const handleAddClick = useCallback((categoryId) => {
    if (!categoryId) return;
    if (!canAppendRowToCategory(categoryId)) return;
    handleAddRow(categoryId);
  }, [canAppendRowToCategory, handleAddRow]);

  const handleFieldChange = useCallback((componentKey, fieldName, rawValue) => {
    const parsed = parsePropertyPartFieldValue(fieldName, rawValue);
    if (parsed.skip) return;
    handlePropertyPartOnChange(componentKey, fieldName, parsed.value);
  }, [handlePropertyPartOnChange]);

  const handleCategoryAddIntent = useCallback((categoryId, addState) => {
    if (addState.mode === HAKLAI_ADD_MODES.ADD) {
      handleAddClick(categoryId);
    }
  }, [handleAddClick]);

  const handleSectionAAddClick = useCallback(() => {
    closeSectionBgAddMenu();
    handleCategoryAddIntent(CATEGORIES.RESIDENTIAL, residentialAddState);
  }, [closeSectionBgAddMenu, handleCategoryAddIntent, residentialAddState]);

  // Defer B+G adds until after the menu-close click finishes dispatching.
  // MUI's Menu closes synchronously on item-click but its focus-return work is
  // queued as a microtask. If we add a new row (and thus a new focusable input)
  // in the same tick, the focus-return fires against the just-mounted input
  // and re-opens the menu or steals focus. Using `setTimeout(0)` pushes the
  // add to the next task, after MUI is done. The ref guards concurrent
  // triggers (rapid double-click on "+"): the newest intent wins.
  const scheduleCategoryAddIntent = useCallback((categoryId, addState) => {
    if (deferredAddTimerRef.current) {
      window.clearTimeout(deferredAddTimerRef.current);
    }

    deferredAddTimerRef.current = window.setTimeout(() => {
      deferredAddTimerRef.current = null;
      handleCategoryAddIntent(categoryId, addState);
    }, 0);
  }, [handleCategoryAddIntent]);

  useEffect(() => () => {
    if (deferredAddTimerRef.current) {
      window.clearTimeout(deferredAddTimerRef.current);
    }
  }, []);

  const handleSectionBgAddSelection = useCallback((categoryId, addState) => {
    closeSectionBgAddMenu();
    scheduleCategoryAddIntent(categoryId, addState);
  }, [closeSectionBgAddMenu, scheduleCategoryAddIntent]);

  const handleSectionBgAddMenuClose = useCallback(() => {
    closeSectionBgAddMenu();
  }, [closeSectionBgAddMenu]);

  const openSectionBgAddMenu = useCallback((event) => {
    if (sectionBgAddMode === HAKLAI_ADD_MODES.UNAVAILABLE || !event?.currentTarget) {
      closeSectionBgAddMenu();
      return;
    }

    openSectionBgAddMenuAnchor(event.currentTarget);
  }, [
    closeSectionBgAddMenu,
    openSectionBgAddMenuAnchor,
    sectionBgAddMode
  ]);

  const renderRow = useCallback((rowKey, part, sectionRowProps) => {
    const rowValidationState = getRowValidationState(rowKey, part);
    const baseCellErrors = validationOverlay.cellErrors[rowKey] || EMPTY_ROW_FIELDS;
    const depreciationState = getPurchaseDepreciationUiState(rowKey, part);
    const rowDepreciationState = (
      pendingRentalDepreciationByPart[rowKey] === true
      && depreciationState.isResidentialAutoAllowed === true
      && !depreciationState.hasRentalConfigured
    )
      ? { ...depreciationState, hasRentalSelected: true }
      : depreciationState;
    const purchaseDepreciationError = (
      rowDepreciationState.isResidentialAutoAllowed === true
      && rowDepreciationState.hasInvalid
    )
      ? rowDepreciationState.validationError || HAKLAI_DEPRECIATION_TOOLTIPS.rentalDepreciationNeedsReview
      : "";
    let rowCellErrors = baseCellErrors;
    if (allocationIssue?.rowKey === rowKey && allocationIssueCellErrors !== EMPTY_ROW_FIELDS) {
      rowCellErrors = { ...rowCellErrors, ...allocationIssueCellErrors };
    }
    if (purchaseDepreciationError) {
      rowCellErrors = { ...rowCellErrors, purchaseDepreciation: purchaseDepreciationError };
    }
    const nameFieldState = isOptionalPartCategory(part.category)
      ? getEditableNameFieldState(rowKey, rowValidationState)
      : { hasError: false, helperText: "", isOptionalTrailing: false };
    const displayLabel = getHaklaiPartDisplayLabel(
      [rowKey, part],
      getCategoryRows(groups, part.category)
    );
    const remainingAfterRow = Object.prototype.hasOwnProperty.call(computationState.runningRemainingByKey, rowKey)
      ? computationState.runningRemainingByKey[rowKey]
      : null;
    const showPre1997ConstructionQuestion = (
      canAskPre1997Construction
      && isHaklaiPre1997MinimalZabanRelevantForPart(part, hasZaban)
    );

    return (
      <HaklaiPropertyPartRow
        key={rowKey}
        classes={classes}
        rowKey={rowKey}
        part={part}
        hasZaban={hasZaban}
        ptorType={ptorType}
        hasTzinunOnPtor={hasTzinunOnPtor}
        lastResidentialKey={groups.lastResidentialKey}
        rowValidationState={rowValidationState}
        nameFieldState={nameFieldState}
        displayLabel={displayLabel}
        showBuiltValueColumn={showBuiltValueColumn}
        showZabanColumn={showZabanColumn}
        showPre1997ConstructionQuestion={showPre1997ConstructionQuestion}
        remainingAfterRow={remainingAfterRow}
        isLastFilledRow={rowKey === computationState.lastFilledRowKey}
        depreciationState={rowDepreciationState}
        onOpenDepreciation={handleOpenPurchaseDepreciation}
        onRentalDepreciationToggle={handleRentalDepreciationToggle}
        onFieldChange={handleFieldChange}
        onFieldBlur={touchRowField}
        onRemove={props.handleRemoveRow}
        setValueInputRef={setValueInputRef}
        setBuiltValueInputRef={setBuiltValueInputRef}
        setRowRef={setRowRef}
        cellErrors={rowCellErrors}
        isFlashing={flashingRowKey === rowKey}
        {...sectionRowProps}
      />
    );
  }, [
    allocationIssue,
    allocationIssueCellErrors,
    classes,
    computationState,
    canAskPre1997Construction,
    getEditableNameFieldState,
    getPurchaseDepreciationUiState,
    getRowValidationState,
    groups,
    handleFieldChange,
    handleOpenPurchaseDepreciation,
    handleRentalDepreciationToggle,
    hasTzinunOnPtor,
    hasZaban,
    pendingRentalDepreciationByPart,
    props.handleRemoveRow,
    ptorType,
    setBuiltValueInputRef,
    setRowRef,
    setValueInputRef,
    showBuiltValueColumn,
    showZabanColumn,
    touchRowField,
    flashingRowKey,
    validationOverlay
  ]);

  const sections = useMemo(() => [
    {
      sectionId: "A",
      sectionTitle: HAKLAI_PROPERTY_PART_LABELS.sectionA,
      addMode: sectionAAddMode,
      addAriaLabel: getHaklaiPropertyPartAddAriaLabel(
        HAKLAI_PROPERTY_PART_LABELS.sectionA,
        sectionAAddMode === HAKLAI_ADD_MODES.ADD
      ),
      onAddClick: handleSectionAAddClick,
      bodies: buildSectionBodies(
        {
          bodyKey: "residential",
          rows: groups.residentialRows,
          label: HAKLAI_PROPERTY_PART_LABELS.residential
        },
        {
          bodyKey: "commercial",
          rows: groups.commercialRows,
          label: HAKLAI_PROPERTY_PART_LABELS.commercial
        }
      ),
      renderRow
    },
    {
      sectionId: "BG",
      sectionTitle: HAKLAI_PROPERTY_PART_LABELS.sectionBg,
      addMode: sectionBgAddMode,
      addAriaLabel: getHaklaiPropertyPartAddAriaLabel(
        HAKLAI_PROPERTY_PART_LABELS.sectionBg,
        sectionBgAddMode === HAKLAI_ADD_MODES.ADD
      ),
      onAddClick: openSectionBgAddMenu,
      bodies: buildSectionBodies(
        {
          bodyKey: "commercialBg",
          rows: groups.commercialBgRows,
          label: HAKLAI_PROPERTY_PART_LABELS.commercial
        },
        {
          bodyKey: "land",
          rows: groups.landRows,
          label: HAKLAI_PROPERTY_PART_LABELS.landAndShared
        }
      ),
      renderRow
    }
  ], [
    groups.commercialBgRows,
    groups.commercialRows,
    groups.landRows,
    groups.residentialRows,
    handleSectionAAddClick,
    openSectionBgAddMenu,
    renderRow,
    sectionAAddMode,
    sectionBgAddMode
  ]);

  const menus = useMemo(() => [
    {
      menuKey: "BG",
      anchorEl: sectionBgAddMenuAnchorEl,
      onClose: handleSectionBgAddMenuClose,
      highlightedCategoryId: sectionBgHighlightedCategoryId,
      items: [
        {
          categoryId: CATEGORIES.COMMERCIAL_BG,
          label: HAKLAI_PROPERTY_PART_LABELS.commercial,
          addState: commercialBgAddState,
          onSelect: () => handleSectionBgAddSelection(CATEGORIES.COMMERCIAL_BG, commercialBgAddState)
        },
        {
          categoryId: CATEGORIES.LAND_AND_SHARED,
          label: HAKLAI_PROPERTY_PART_LABELS.landAndShared,
          addState: landAddState,
          onSelect: () => handleSectionBgAddSelection(CATEGORIES.LAND_AND_SHARED, landAddState)
        }
      ]
    }
  ], [
    commercialBgAddState,
    handleSectionBgAddMenuClose,
    handleSectionBgAddSelection,
    landAddState,
    sectionBgAddMenuAnchorEl,
    sectionBgHighlightedCategoryId
  ]);

  const activePurchaseDepreciationPart = activePurchaseDepreciationPartKey
    ? parts[activePurchaseDepreciationPartKey]
    : null;
  const activePurchaseDepreciationState = activePurchaseDepreciationPart
    ? getPurchaseDepreciationUiState(activePurchaseDepreciationPartKey, activePurchaseDepreciationPart)
    : null;
  const activePurchaseDepreciationValue = activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    ? activePurchaseDepreciationState?.rentalDepreciation || {}
    : activePurchaseDepreciationState?.manualDepreciation || {};
  const activePurchaseDepreciationTitle = activePurchaseDepreciationMode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO
    ? HAKLAI_DEPRECIATION_LABELS.residentialRentalDepreciation
    : HAKLAI_DEPRECIATION_LABELS.form11Depreciation;

  return (
    <Box ref={sectionRootRef} className={classes.section}>
      <Box className={classes.sectionHeader}>
        <ButtonBase className={classes.sectionHeaderButton} onClick={props.onToggleExpanded}>
          <Typography className={classes.sectionHeaderTitle}>
            {HAKLAI_PROPERTY_PART_LABELS.sectionTitle}
          </Typography>
          <ExpandMoreIcon
            className={[
              classes.sectionToggleIcon,
              props.expanded ? classes.sectionToggleIconExpanded : ""
            ].filter(Boolean).join(" ")}
          />
        </ButtonBase>
      </Box>

      <Collapse
        in={props.expanded}
        timeout={PROPERTY_PARTS_SECTION_TRANSITION_MS}
        unmountOnExit={false}
        onEnter={() => setStickyHeaderEnabled(false)}
        onEntered={() => setStickyHeaderEnabled(true)}
        onExit={() => setStickyHeaderEnabled(false)}
      >
        <Box className={classes.sectionContent}>
          <HaklaiLockNotice
            classes={classes}
            message={props.locked ? props.lockNotice?.message : ""}
          />

          <Box
            ref={lockableContentRef}
            className={props.locked ? classes.lockedSectionContent : ""}
            // React 17 does not know about the `inert` attribute, so passing a
            // boolean renders the literal word "true"/"false" into the DOM.
            // Passing an empty string (when locked) produces the correct
            // `inert=""` attribute, and `undefined` omits it entirely.
            // React 18+ natively supports `inert` as a boolean prop — when
            // this project upgrades, replace with `inert={props.locked}`.
            inert={props.locked ? "" : undefined}
          >
            <Grid container direction="column" spacing={2} alignItems="center">
              {/*
                `alignSelf: "stretch"` overrides the parent's `alignItems: center`
                for THIS item only. The table wrapper fills that stretched width,
                so the section background and table chrome stay the same width
                when optional זב"ן columns are toggled.
              */}
              <Grid item style={{ alignSelf: "stretch", minWidth: 0, maxWidth: "100%" }}>
                <HaklaiPropertyPartsTable
                  classes={classes}
                  stickyHeaderEnabled={stickyHeaderEnabled}
                  showBuiltValueColumn={showBuiltValueColumn}
                  showZabanColumn={showZabanColumn}
                  hasPre1997ConstructionQuestion={hasPre1997ConstructionQuestion}
                  sections={sections}
                  menus={menus}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Collapse>
      {activePurchaseDepreciationPart ? (
        <HaklaiPropertyDepreciationDialog
          open
          onClose={handlePurchaseDepreciationDialogClose}
          part={activePurchaseDepreciationPart}
          displayLabel={`${activePurchaseDepreciationTitle} - ${getHaklaiPartDisplayLabel(
            [activePurchaseDepreciationPartKey, activePurchaseDepreciationPart],
            getCategoryRows(groups, activePurchaseDepreciationPart.category)
          )}`}
          depreciation={activePurchaseDepreciationValue}
          ownershipStartDate={purchaseDepreciationStartDate}
          soldPropertyDate={props.soldPropertyDate}
          acquisitionCurrency={purchaseCurrencyValue}
          modeOverride={activePurchaseDepreciationMode}
          onSave={(nextDepreciation) => handlePurchaseDepreciationChange?.(
            activePurchaseDepreciationPartKey,
            nextDepreciation,
            activePurchaseDepreciationMode
          )}
        />
      ) : null}
    </Box>
  );
}));

HaklaiPropertyPartsSection.displayName = "HaklaiPropertyPartsSection";

export default HaklaiPropertyPartsSection;
